import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "container",
    "status",
    "saveBtn",
    "exportBtn",
    "player",
    "wordMenu",
    "menuCutBtn",
    "menuMuteBtn",
    "menuUnmuteBtn",
    "menuSplitBtn",
    "menuRestoreBtn",
    "pauseMenu",
    "pauseTrimBtn",
    "pauseRemoveBtn",
    "pauseRestoreBtn",
    "speakerPopup",
    "speakerNameInput",
    "speakerVolumeInput",
    "popupSpeakerVolLabel",
    "popupSegVolLabel",
    "popupEffectiveLabel",
    "restoreSegmentBtn",
    "hideCutsBtn",
    "undoBtn",
    "redoBtn",
    "searchToggleBtn",
    "searchBox",
    "searchInput",
    "searchCount",
    "pauseFilterBtn",
    "playBtn",
    "loopBtn",
    "speedSelect",
    "seekBar",
    "timeDisplay",
    "durationDisplay",
  ];
  static values = {
    updateUrl: String,
    exportUrl: String,
    hasVideo: Boolean,
    savedVolume: Object,
  };

  connect() {
    this.dirty = false;
    this.saveTimeout = null;
    this._currentWord = null;
    this._activeToken = null; // token the word-menu is open for
    this._activeSegment = null; // segment the speaker-popup is open for
    this._activeSpeakerChip = null;

    // In-memory edit state (cuts + volume)
    this.editState = {
      cuts: new Map(),
      mutes: new Map(),
      volume: { speakers: {}, segments: {} },
    };

    // Restore saved state from DOM
    this._loadSavedState();

    // Build sorted word index for O(log n) timeupdate lookup
    this._buildWordIndex();

    // Pause filter
    this._pauseThreshold = null;

    // Player loop / seek state
    this._loopActive = false;
    this._loopEnd = null;
    this._seeking = false;

    // Attach player listeners
    this._boundTimeUpdate = this._onTimeUpdate.bind(this);
    this._boundOnPlay = this._onPlayerPlay.bind(this);
    this._boundOnPause = this._onPlayerPause.bind(this);
    this._boundLoadedMeta = this._onLoadedMeta.bind(this);
    if (this.hasPlayerTarget) {
      this.playerTarget.addEventListener("timeupdate", this._boundTimeUpdate);
      this.playerTarget.addEventListener("play", this._boundOnPlay);
      this.playerTarget.addEventListener("pause", this._boundOnPause);
      this.playerTarget.addEventListener(
        "loadedmetadata",
        this._boundLoadedMeta,
      );
    }

    // Undo / redo stacks
    this._undoStack = [];
    this._redoStack = [];
    this._updateUndoRedoUI();

    // Search state
    this._searchMatches = [];
    this._searchIndex = 0;

    // Close word menu on outside click
    this._boundDocClick = this._onDocClick.bind(this);
    this._boundKeydown = this._onGlobalKeydown.bind(this);
    document.addEventListener("click", this._boundDocClick, true);
    document.addEventListener("keydown", this._boundKeydown);
  }

  disconnect() {
    if (this.hasPlayerTarget) {
      this.playerTarget.removeEventListener(
        "timeupdate",
        this._boundTimeUpdate,
      );
      this.playerTarget.removeEventListener("play", this._boundOnPlay);
      this.playerTarget.removeEventListener("pause", this._boundOnPause);
      this.playerTarget.removeEventListener(
        "loadedmetadata",
        this._boundLoadedMeta,
      );
    }
    document.removeEventListener("click", this._boundDocClick, true);
    document.removeEventListener("keydown", this._boundKeydown);
  }

  // ── Load saved edit state from DOM ───────────────────────────────

  _loadSavedState() {
    // Cuts: read data-cut="true" from word-tokens and pause-pills only
    this.containerTarget
      .querySelectorAll(
        ".word-token[data-cut='true'], .pause-pill[data-cut='true']",
      )
      .forEach((el) => {
        const id = el.dataset.id;
        const from = parseInt(el.dataset.from);
        const to = parseInt(el.dataset.to);
        if (id) this.editState.cuts.set(id, { id, from, to });
      });

    // Mutes
    this.containerTarget
      .querySelectorAll(".word-token[data-muted='true']")
      .forEach((el) => {
        const id = el.dataset.id;
        const from = parseInt(el.dataset.from);
        const to = parseInt(el.dataset.to);
        if (id) this.editState.mutes.set(id, { id, from, to });
      });

    // Restore saved volume from server-rendered edit_data
    const saved = this.savedVolumeValue || {};
    if (saved.speakers)
      Object.assign(this.editState.volume.speakers, saved.speakers);
    if (saved.segments)
      Object.assign(this.editState.volume.segments, saved.segments);
  }

  // ── Word index (sorted by `from`) for binary search ─────────────

  _buildWordIndex() {
    const tokens = Array.from(
      this.containerTarget.querySelectorAll(".word-token"),
    );
    this._wordIndex = tokens
      .map((el) => ({
        from: parseInt(el.dataset.from),
        to: parseInt(el.dataset.to),
        el,
      }))
      .sort((a, b) => a.from - b.from);
  }

  _findWordAt(ms) {
    const arr = this._wordIndex;
    let lo = 0,
      hi = arr.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid].to < ms) {
        lo = mid + 1;
      } else if (arr[mid].from > ms) {
        hi = mid - 1;
      } else {
        return arr[mid].el;
      }
    }
    return null;
  }

  // ── Playback sync ────────────────────────────────────────────────

  _onTimeUpdate(event) {
    const player = event.target;
    const ms = player.currentTime * 1000;

    // 5-second loop
    if (this._loopActive && this._loopEnd !== null && !player.paused) {
      if (player.currentTime >= this._loopEnd) {
        player.currentTime = Math.max(0, this._loopEnd - 5);
        return;
      }
    }

    // Skip over any cut range during playback
    if (!player.paused) {
      for (const cut of this.editState.cuts.values()) {
        if (ms >= cut.from && ms < cut.to) {
          player.currentTime = cut.to / 1000;
          return;
        }
      }
    }

    // Seek bar + time display
    if (this.hasSeekBarTarget && !this._seeking) {
      const bar = this.seekBarTarget;
      bar.value = player.currentTime;
      const pct = player.duration
        ? (player.currentTime / player.duration) * 100
        : 0;
      bar.style.setProperty("--progress", `${pct.toFixed(2)}%`);
    }
    if (this.hasTimeDisplayTarget) {
      this.timeDisplayTarget.textContent = this._fmtTime(player.currentTime);
    }

    // Word highlight
    const tok = this._findWordAt(ms);
    if (this._currentWord && this._currentWord !== tok) {
      this._unhighlightWord(this._currentWord);
      this._currentWord = null;
    }
    if (tok && tok !== this._currentWord && tok.dataset.cut !== "true") {
      this._highlightWord(tok);
      this._currentWord = tok;
      tok.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }

  _highlightWord(tok) {
    const span = tok.querySelector(".word-text");
    if (!span) return;
    span.style.backgroundColor = "#d4d0fc";
    span.style.borderRadius = "3px";
    span.style.color = "#3730a3";
  }

  _unhighlightWord(tok) {
    const span = tok.querySelector(".word-text");
    if (!span) return;
    span.style.backgroundColor = "";
    span.style.borderRadius = "";
    span.style.color = "";
  }

  // ── Seek on word click ───────────────────────────────────────────

  seekToWord(token, ms) {
    if (!this.hasPlayerTarget) return;
    this.playerTarget.currentTime = ms / 1000;
  }

  // ── Word click → show action menu ────────────────────────────────

  onWordClick(event) {
    const token = event.currentTarget;
    const isCut = token.dataset.cut === "true";

    // Seek to word and highlight it immediately (skip cut words)
    if (this.hasPlayerTarget && !isCut) {
      const from = parseInt(token.dataset.from);
      if (!isNaN(from)) {
        this.playerTarget.currentTime = from / 1000;
        if (this._currentWord && this._currentWord !== token) {
          this._unhighlightWord(this._currentWord);
        }
        this._highlightWord(token);
        this._currentWord = token;
      }
    }

    // Open action menu
    const isMuted = token.dataset.muted === "true";
    const mode = isCut ? "restore" : isMuted ? "muted" : "options";
    this._openWordMenu(token, mode);
    event.stopPropagation();
  }

  _openWordMenu(token, mode) {
    this._activeToken = token;
    // Hide all option buttons, show only the right ones
    this.menuCutBtnTarget.classList.toggle("hidden", mode !== "options");
    this.menuMuteBtnTarget.classList.toggle("hidden", mode !== "options");
    this.menuUnmuteBtnTarget.classList.toggle("hidden", mode !== "muted");
    this.menuSplitBtnTarget.classList.toggle("hidden", mode === "restore");
    this.menuRestoreBtnTarget.classList.toggle("hidden", mode !== "restore");

    const rect = token.getBoundingClientRect();
    const menu = this.wordMenuTarget;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.left = `${rect.left}px`;
    menu.classList.remove("hidden");
  }

  menuCut(event) {
    if (this._activeToken) {
      this._pushUndo();
      this._cutToken(this._activeToken);
      this.markDirty();
    }
    this._closeMenu();
    event.stopPropagation();
  }

  menuMute(event) {
    if (this._activeToken) {
      this._pushUndo();
      this._muteToken(this._activeToken);
      this.markDirty();
    }
    this._closeMenu();
    event.stopPropagation();
  }

  menuUnmute(event) {
    if (this._activeToken) {
      this._pushUndo();
      this._unmuteToken(this._activeToken);
      this.markDirty();
    }
    this._closeMenu();
    event.stopPropagation();
  }

  menuSplit(event) {
    if (this._activeToken) {
      const wordText = this._activeToken.querySelector(".word-text");
      if (wordText) {
        this._splitSegmentAtWord(wordText);
        this.markDirty();
      }
    }
    this._closeMenu();
    event.stopPropagation();
  }

  menuRestore(event) {
    if (!this._activeToken) return;
    this._pushUndo();
    this._restoreToken(this._activeToken);
    this._closeMenu();
    this.markDirty();
    event.stopPropagation();
  }

  menuClose(event) {
    this._closeMenu();
    event.stopPropagation();
  }

  _closeMenu() {
    this.wordMenuTarget.classList.add("hidden");
    this._activeToken = null;
  }

  _onDocClick(e) {
    if (
      !this.wordMenuTarget.classList.contains("hidden") &&
      !this.wordMenuTarget.contains(e.target)
    ) {
      this._closeMenu();
    }
    if (
      !this.pauseMenuTarget.classList.contains("hidden") &&
      !this.pauseMenuTarget.contains(e.target)
    ) {
      this._closePauseMenu();
    }
  }

  // ── Cut / restore helpers ────────────────────────────────────────

  _cutToken(token) {
    const id = token.dataset.id;
    const from = parseInt(token.dataset.from);
    const to = parseInt(token.dataset.to);
    this.editState.cuts.set(id, { id, from, to });
    token.dataset.cut = "true";
    token.classList.add("opacity-40");
    const span = token.querySelector(".word-text");
    if (span) {
      span.classList.add("line-through", "text-gray-400");
      span.classList.remove("text-gray-800");
      span.contentEditable = "false";
    }
  }

  _restoreToken(token) {
    const id = token.dataset.id;
    this.editState.cuts.delete(id);
    token.dataset.cut = "false";
    token.classList.remove("opacity-40");
    const span = token.querySelector(".word-text");
    if (span) {
      span.classList.remove("line-through", "text-gray-400");
      span.classList.add("text-gray-800");
      span.contentEditable = "true";
    }
  }

  _muteToken(token) {
    const id = token.dataset.id;
    const from = parseInt(token.dataset.from);
    const to = parseInt(token.dataset.to);
    this.editState.mutes.set(id, { id, from, to });
    token.dataset.muted = "true";
  }

  _unmuteToken(token) {
    this.editState.mutes.delete(token.dataset.id);
    token.dataset.muted = "false";
  }

  // ── Pause pill click → open menu ─────────────────────────────────

  onPauseClick(event) {
    event.stopPropagation();
    const pill = event.currentTarget;
    const wrapper = pill.closest(".pause-wrapper");
    this._activePause = { pill, wrapper };

    const isCut = pill.dataset.cut === "true";
    this.pauseTrimBtnTarget.classList.toggle("hidden", isCut);
    this.pauseRemoveBtnTarget.classList.toggle("hidden", isCut);
    this.pauseRestoreBtnTarget.classList.toggle("hidden", !isCut);

    const rect = pill.getBoundingClientRect();
    const menu = this.pauseMenuTarget;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.left = `${rect.left}px`;
    menu.classList.remove("hidden");
  }

  pauseMenuTrim(event) {
    event.stopPropagation();
    if (!this._activePause) return;
    this._pushUndo();
    const { pill, wrapper } = this._activePause;
    const id = pill.dataset.id;
    const from = parseInt(pill.dataset.from);
    const to = Math.max(from, parseInt(pill.dataset.to) - 200);
    this.editState.cuts.set(id, { id, from, to });
    this._setPauseCutState(pill, wrapper, true);
    this.markDirty();
    this._closePauseMenu();
  }

  pauseMenuRemove(event) {
    event.stopPropagation();
    if (!this._activePause) return;
    this._pushUndo();
    const { pill, wrapper } = this._activePause;
    const id = pill.dataset.id;
    const from = parseInt(pill.dataset.from);
    const to = parseInt(pill.dataset.to);
    this.editState.cuts.set(id, { id, from, to });
    this._setPauseCutState(pill, wrapper, true);
    this.markDirty();
    this._closePauseMenu();
  }

  pauseMenuRestore(event) {
    event.stopPropagation();
    if (!this._activePause) return;
    this._pushUndo();
    const { pill, wrapper } = this._activePause;
    this.editState.cuts.delete(pill.dataset.id);
    this._setPauseCutState(pill, wrapper, false);
    this.markDirty();
    this._closePauseMenu();
  }

  pauseMenuClose(event) {
    event.stopPropagation();
    this._closePauseMenu();
  }

  _closePauseMenu() {
    this.pauseMenuTarget.classList.add("hidden");
    this._activePause = null;
  }

  _setPauseCutState(pill, wrapper, isCut) {
    pill.dataset.cut = String(isCut);
    wrapper.dataset.cut = String(isCut);
    if (isCut) {
      pill.classList.add("bg-red-100", "text-red-400", "line-through");
      pill.classList.remove(
        "bg-gray-200",
        "text-gray-500",
        "hover:bg-gray-300",
      );
    } else {
      pill.classList.remove("bg-red-100", "text-red-400", "line-through");
      pill.classList.add("bg-gray-200", "text-gray-500", "hover:bg-gray-300");
    }
  }

  // ── Speaker popup ────────────────────────────────────────────────

  onSpeakerClick(event) {
    const chip = event.currentTarget;
    const segment = chip.closest(".segment");
    this._activeSegment = segment;
    this._activeSpeakerChip = chip;

    const spk = chip.dataset.origSpeaker;
    const segIdx = chip.dataset.segment;

    this.speakerNameInputTarget.value = spk;

    const spkDB = this._linToDB(this.editState.volume.speakers[spk] || 1.0);
    this.speakerVolumeInputTarget.value = spkDB;
    this.speakerVolumeInputTarget.dataset.speaker = spk;
    this.popupSpeakerVolLabelTarget.textContent = this._fmtDB(spkDB);

    this._activeSegIdx = segIdx;
    this._segBoostDB = this._linToDB(
      this.editState.volume.segments[segIdx] || 1.0,
    );
    this._updateSegBoostUI();

    const rect = chip.getBoundingClientRect();
    const popup = this.speakerPopupTarget;
    popup.style.top = `${rect.bottom + 6}px`;
    popup.style.left = `${rect.left}px`;
    popup.classList.remove("hidden");

    const hasCuts =
      segment.querySelector(".word-token[data-cut='true']") !== null;
    this.restoreSegmentBtnTarget.classList.toggle("hidden", !hasCuts);

    this.speakerNameInputTarget.focus();
    this.speakerNameInputTarget.select();
    event.stopPropagation();

    // Close on outside click — listener stays active until popup closes
    this._removeSpeakerOutside?.();
    const handler = (e) => {
      if (!popup.contains(e.target) && !e.target.closest(".speaker-label")) {
        this.speakerPopupClose();
      }
    };
    setTimeout(() => document.addEventListener("mousedown", handler, true), 0);
    this._removeSpeakerOutside = () =>
      document.removeEventListener("mousedown", handler, true);
  }

  onSpeakerNameInput() {
    // Live-preview the name in the active chip only
    if (this._activeSpeakerChip)
      this._activeSpeakerChip.textContent = this.speakerNameInputTarget.value;
  }

  onPopupSpeakerVolume(event) {
    const db = parseInt(event.currentTarget.value);
    const spk = event.currentTarget.dataset.speaker;
    this.editState.volume.speakers[spk] = this._dbToLin(db);
    this.popupSpeakerVolLabelTarget.textContent = this._fmtDB(db);
    this._updateSegBoostUI();
    this.markDirty();
  }

  segBoostDown() {
    this._stepSegBoost(-1);
  }
  segBoostUp() {
    this._stepSegBoost(+1);
  }

  _stepSegBoost(delta) {
    this._segBoostDB = Math.max(
      -20,
      Math.min(20, (this._segBoostDB || 0) + delta),
    );
    this.editState.volume.segments[this._activeSegIdx] = this._dbToLin(
      this._segBoostDB,
    );
    this._updateSegBoostUI();
    this.markDirty();
  }

  _updateSegBoostUI() {
    const boost = this._segBoostDB || 0;
    this.popupSegVolLabelTarget.textContent = this._fmtDB(boost);
    const spk = this._activeSpeakerChip?.dataset.origSpeaker;
    const spkDB = this._linToDB(this.editState.volume.speakers[spk] || 1.0);
    const effectiveDB = spkDB + boost;
    this.popupEffectiveLabelTarget.textContent = `Effective: ${this._fmtDB(effectiveDB)}`;
  }

  _linToDB(linear) {
    if (linear <= 0) return -20;
    return Math.round(20 * Math.log10(linear));
  }

  _dbToLin(db) {
    return Math.pow(10, db / 20);
  }

  _fmtDB(db) {
    if (db === 0) return "0 dB";
    return db > 0 ? `+${db} dB` : `${db} dB`;
  }

  cutSegment() {
    if (!this._activeSegment) return;
    this._pushUndo();
    this._activeSegment.querySelectorAll(".word-token").forEach((token) => {
      if (token.dataset.cut !== "true") this._cutToken(token);
    });
    this.speakerPopupClose();
    this.markDirty();
  }

  restoreSegment() {
    if (!this._activeSegment) return;
    this._pushUndo();
    this._activeSegment.querySelectorAll(".word-token").forEach((token) => {
      if (token.dataset.cut === "true") this._restoreToken(token);
    });
    this.speakerPopupClose();
    this.markDirty();
  }

  speakerPopupClose() {
    // Commit name change before clearing state
    if (this._activeSpeakerChip) {
      const newName = this.speakerNameInputTarget.value.trim();
      const origName = this._activeSpeakerChip.dataset.origSpeaker;
      if (newName && newName !== origName) {
        this.containerTarget
          .querySelectorAll(".speaker-label")
          .forEach((el) => {
            if (el.dataset.origSpeaker === origName) {
              el.textContent = newName;
              el.dataset.origSpeaker = newName;
            }
          });
        if (this.editState.volume.speakers[origName] !== undefined) {
          this.editState.volume.speakers[newName] =
            this.editState.volume.speakers[origName];
          delete this.editState.volume.speakers[origName];
        }
        this.markDirty();
      }
    }

    this.speakerPopupTarget.classList.add("hidden");
    this._activeSegment = null;
    this._activeSpeakerChip = null;
    this._removeSpeakerOutside?.();
    this._removeSpeakerOutside = null;
  }

  // ── Segment split / merge ────────────────────────────────────────

  onWordKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this._splitSegmentAtWord(event.currentTarget);
      this.markDirty();
    } else if (
      event.key === "Backspace" &&
      this._caretAtStart(event.currentTarget)
    ) {
      const token = event.currentTarget.closest(".word-token");
      const wordsContainer = token.closest(".words-container");
      if (wordsContainer.querySelector(".word-token") === token) {
        event.preventDefault();
        this._mergeWithPreviousSegment(token.closest(".segment"));
        this.markDirty();
      }
    }
  }

  _caretAtStart(el) {
    const sel = window.getSelection();
    return (
      sel.rangeCount > 0 &&
      sel.isCollapsed &&
      sel.getRangeAt(0).startOffset === 0
    );
  }

  _splitSegmentAtWord(wordTextEl) {
    const token = wordTextEl.closest(".word-token");
    const segment = token.closest(".segment");
    const wordsContainer = segment.querySelector(".words-container");

    // Everything after the current token (tokens + pause pills)
    const allChildren = Array.from(wordsContainer.children);
    const tokenIdx = allChildren.indexOf(token);
    const itemsAfter = allChildren.slice(tokenIdx);
    if (itemsAfter.length === 0) return;

    // Clone the segment shell (keeps class, style)
    const newSeg = document.createElement("div");
    newSeg.className = segment.className;
    newSeg.dataset.segment = `split_${Date.now()}`;
    newSeg.style.cssText = segment.style.cssText;

    // Clone the speaker header row (chip + timestamp)
    const headerRow = segment.querySelector(".flex.items-center.gap-3");
    if (headerRow) {
      newSeg.appendChild(headerRow.cloneNode(true));
    }

    // New words container with the moved items
    const newContainer = document.createElement("div");
    newContainer.className = wordsContainer.className;
    itemsAfter.forEach((el) => newContainer.appendChild(el));
    newSeg.appendChild(newContainer);

    segment.after(newSeg);

    // Focus the first word in the new segment
    const firstWord = newContainer.querySelector(".word-text");
    if (firstWord) this._focusStart(firstWord);

    this._buildWordIndex();
  }

  _mergeWithPreviousSegment(segment) {
    const prevSeg = segment.previousElementSibling;
    if (!prevSeg || !prevSeg.classList.contains("segment")) return;

    const prevContainer = prevSeg.querySelector(".words-container");
    const thisContainer = segment.querySelector(".words-container");

    // Remember where to focus: last word-token in previous segment
    const prevTokens = prevContainer.querySelectorAll(".word-token");
    const focusTarget =
      prevTokens[prevTokens.length - 1]?.querySelector(".word-text");

    // Move all children across
    Array.from(thisContainer.children).forEach((el) =>
      prevContainer.appendChild(el),
    );
    segment.remove();

    if (focusTarget) this._focusEnd(focusTarget);
    this._buildWordIndex();
  }

  _focusStart(el) {
    el.focus();
    const range = document.createRange();
    range.setStart(el.firstChild || el, 0);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  _focusEnd(el) {
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  // ── Speaker editing ──────────────────────────────────────────────

  onSpeakerInput() {
    this.markDirty();
  }

  updateSpeaker(event) {
    const speakerEl = event.currentTarget;
    const newName = speakerEl.textContent.trim();
    const origName = speakerEl.dataset.origSpeaker;

    if (origName && newName && newName !== origName) {
      this.containerTarget.querySelectorAll(".speaker-label").forEach((el) => {
        if (el.dataset.origSpeaker === origName) {
          el.textContent = newName;
          el.dataset.origSpeaker = newName;
        }
      });
      // Update volume keys if renamed
      if (this.editState.volume.speakers[origName] !== undefined) {
        this.editState.volume.speakers[newName] =
          this.editState.volume.speakers[origName];
        delete this.editState.volume.speakers[origName];
      }
      // Update speaker-vol label data attributes
      this.speakerVolLabelTargets.forEach((el) => {
        if (el.dataset.speaker === origName) el.dataset.speaker = newName;
      });
    }
    this.markDirty();
  }

  // ── Dirty / Save ─────────────────────────────────────────────────

  markDirty() {
    this.dirty = true;
    this.showStatus("Unsaved changes…", "text-amber-500");
    clearTimeout(this.saveTimeout);
    this.saveTimeout = setTimeout(() => this.save(), 2500);
  }

  async save() {
    clearTimeout(this.saveTimeout);
    const transcriptData = this.collectTranscriptData();
    const editData = this.collectEditData();

    this.showStatus("Saving…", "text-gray-400");
    this.saveBtnTarget.disabled = true;

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify({
          transcript_data: transcriptData,
          edit_data: editData,
        }),
      });
      if (response.ok) {
        this.dirty = false;
        this.showStatus("Saved", "text-green-500");
        setTimeout(() => {
          if (!this.dirty) this.showStatus("", "");
        }, 2000);
      } else {
        this.showStatus("Save failed", "text-red-500");
      }
    } catch {
      this.showStatus("Save failed", "text-red-500");
    } finally {
      this.saveBtnTarget.disabled = false;
    }
  }

  // ── Export video ─────────────────────────────────────────────────

  async exportVideo() {
    await this.save();

    if (this.hasExportBtnTarget) {
      this.exportBtnTarget.disabled = true;
      this.exportBtnTarget.textContent = "Exporting…";
    }
    this.showStatus("Export started…", "text-indigo-500");

    try {
      await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      });
    } catch {
      this.showStatus("Export failed", "text-red-500");
      if (this.hasExportBtnTarget) {
        this.exportBtnTarget.disabled = false;
        this.exportBtnTarget.textContent = "Export Video";
      }
    }
  }

  // ── Toggle cut visibility ─────────────────────────────────────────

  toggleHideCuts() {
    const hiding = this.containerTarget.classList.toggle("hide-cuts");
    this.hideCutsBtnTarget.innerHTML = hiding
      ? `<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg> Show cuts`
      : `<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg> Hide cuts`;
  }

  // ── Download TXT ─────────────────────────────────────────────────

  downloadTxt() {
    const lines = [];
    this.containerTarget.querySelectorAll("[data-segment]").forEach((segEl) => {
      if (!segEl.classList.contains("segment")) return;
      const speakerEl = segEl.querySelector(".speaker-label");
      const speaker = speakerEl ? speakerEl.textContent.trim() : "Speaker 1";
      const words = [];
      segEl.querySelectorAll(".word-token").forEach((tokenEl) => {
        if (tokenEl.dataset.cut === "true") return;
        const text = tokenEl.querySelector(".word-text")?.textContent.trim();
        if (text) words.push(text);
      });
      if (words.length > 0) lines.push(`${speaker}\n${words.join(" ")}\n`);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transcript.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Data collection ──────────────────────────────────────────────

  collectTranscriptData() {
    // Always save ALL words (cut and uncut) so indices stay stable across reloads.
    // Cuts are stored separately in edit_data and applied as an overlay.
    const segments = [];
    this.containerTarget.querySelectorAll("[data-segment]").forEach((segEl) => {
      if (!segEl.classList.contains("segment")) return;
      const speakerEl = segEl.querySelector(".speaker-label");
      const speaker = speakerEl ? speakerEl.textContent.trim() : "Speaker 1";
      const words = [];
      segEl.querySelectorAll(".word-token").forEach((tokenEl) => {
        const text = tokenEl.querySelector(".word-text")?.textContent.trim();
        if (!text) return;
        words.push({
          text,
          from: parseInt(tokenEl.dataset.from || 0),
          to: parseInt(tokenEl.dataset.to || 0),
        });
      });
      if (words.length > 0) segments.push({ speaker, words });
    });
    return { segments };
  }

  collectEditData() {
    return {
      cuts: [...this.editState.cuts.values()],
      mutes: [...this.editState.mutes.values()],
      volume: {
        speakers: { ...this.editState.volume.speakers },
        segments: { ...this.editState.volume.segments },
      },
    };
  }

  // ── Helpers ──────────────────────────────────────────────────────

  showStatus(msg, colorClass) {
    const el = this.statusTarget;
    el.textContent = msg;
    el.className = `text-xs transition-colors ${colorClass}`;
  }

  // ── Custom video controls ────────────────────────────────────────

  playerPlayPause() {
    if (!this.hasPlayerTarget) return;
    const player = this.playerTarget;
    if (player.paused) {
      player.play();
    } else {
      player.pause();
    }
  }

  _onPlayerPlay() {
    if (!this.hasPlayBtnTarget) return;
    this.playBtnTarget.innerHTML = `<svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg><span>Pause</span>`;
  }

  _onPlayerPause() {
    if (!this.hasPlayBtnTarget) return;
    this.playBtnTarget.innerHTML = `<svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg><span>Play</span>`;
  }

  _onLoadedMeta() {
    const dur = this.playerTarget.duration || 0;
    if (this.hasSeekBarTarget) this.seekBarTarget.max = dur;
    if (this.hasDurationDisplayTarget)
      this.durationDisplayTarget.textContent = this._fmtTime(dur);
  }

  playerStop() {
    if (!this.hasPlayerTarget) return;
    this.playerTarget.pause();
    this.playerTarget.currentTime = 0;
    this._loopActive = false;
    this._loopEnd = null;
    if (this.hasLoopBtnTarget) this._setLoopBtnState(false);
  }

  playerSetSpeed(event) {
    if (!this.hasPlayerTarget) return;
    this.playerTarget.playbackRate = parseFloat(event.currentTarget.value);
  }

  playerToggleLoop() {
    if (!this.hasPlayerTarget) return;
    this._loopActive = !this._loopActive;
    if (this._loopActive) {
      this._loopEnd = this.playerTarget.currentTime;
    } else {
      this._loopEnd = null;
    }
    if (this.hasLoopBtnTarget) this._setLoopBtnState(this._loopActive);
  }

  _setLoopBtnState(active) {
    this.loopBtnTarget.classList.toggle("bg-indigo-100", active);
    this.loopBtnTarget.classList.toggle("text-indigo-700", active);
    this.loopBtnTarget.classList.toggle("bg-gray-100", !active);
    this.loopBtnTarget.classList.toggle("text-gray-700", !active);
  }

  // ── Seek bar ─────────────────────────────────────────────────────

  seekStart() {
    this._seeking = true;
  }
  seekEnd() {
    this._seeking = false;
  }
  onSeek(event) {
    const bar = event.currentTarget;
    if (this.hasPlayerTarget)
      this.playerTarget.currentTime = parseFloat(bar.value);
    const pct = bar.max
      ? (parseFloat(bar.value) / parseFloat(bar.max)) * 100
      : 0;
    bar.style.setProperty("--progress", `${pct.toFixed(2)}%`);
  }

  // ── Transcript navigation ─────────────────────────────────────────

  navNextPause() {
    const nowMs = this.hasPlayerTarget
      ? this.playerTarget.currentTime * 1000
      : 0;
    const threshold = this._pauseThreshold || 0;
    const pills = [
      ...this.containerTarget.querySelectorAll(".pause-pill"),
    ].filter(
      (p) => parseInt(p.dataset.to) - parseInt(p.dataset.from) >= threshold,
    );
    const next = pills.find((p) => parseInt(p.dataset.from) > nowMs + 100);
    const target = next || pills[0];
    if (target) this._navTo(parseInt(target.dataset.from), target);
  }

  setPauseFilter(event) {
    const threshold = parseInt(event.currentTarget.dataset.threshold);
    // Toggle off if already active
    if (this._pauseThreshold === threshold) {
      this._pauseThreshold = null;
      this._applyPauseFilter();
      this.pauseFilterBtnTargets.forEach((b) =>
        this._setPauseFilterBtnActive(b, false),
      );
      return;
    }
    this._pauseThreshold = threshold;
    this._applyPauseFilter();
    this.pauseFilterBtnTargets.forEach((b) =>
      this._setPauseFilterBtnActive(
        b,
        parseInt(b.dataset.threshold) === threshold,
      ),
    );
  }

  _setPauseFilterBtnActive(btn, active) {
    btn.classList.toggle("bg-gray-800", active);
    btn.classList.toggle("text-white", active);
    btn.classList.toggle("border-gray-800", active);
    btn.classList.toggle("bg-white", !active);
    btn.classList.toggle("text-gray-700", !active);
  }

  _applyPauseFilter() {
    const threshold = this._pauseThreshold;
    this.containerTarget.querySelectorAll(".pause-pill").forEach((pill) => {
      const dur = parseInt(pill.dataset.to) - parseInt(pill.dataset.from);
      pill.classList.remove("pause-above-threshold", "pause-below-threshold");
      if (threshold !== null) {
        pill.classList.add(
          dur >= threshold ? "pause-above-threshold" : "pause-below-threshold",
        );
      }
    });
  }

  navNextCut() {
    const nowMs = this.hasPlayerTarget
      ? this.playerTarget.currentTime * 1000
      : 0;
    const cuts = [
      ...this.containerTarget.querySelectorAll(".word-token[data-cut='true']"),
    ];
    const next = cuts.find((t) => parseInt(t.dataset.from) > nowMs + 100);
    const target = next || cuts[0];
    if (target) this._navTo(parseInt(target.dataset.from), target);
  }

  navNextSpeaker() {
    const nowMs = this.hasPlayerTarget
      ? this.playerTarget.currentTime * 1000
      : 0;
    const segments = [...this.containerTarget.querySelectorAll(".segment")];
    const withTimes = segments.map((seg) => {
      const first = seg.querySelector(".word-token");
      return { seg, from: first ? parseInt(first.dataset.from) : 0 };
    });
    const next = withTimes.find(({ from }) => from > nowMs + 100);
    const target = next || withTimes[0];
    if (target) this._navTo(target.from, target.seg);
  }

  _navTo(ms, el) {
    if (this.hasPlayerTarget && !isNaN(ms)) {
      this.playerTarget.currentTime = ms / 1000;
      this._scheduleSkips();
    }
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // ── Time formatting ───────────────────────────────────────────────

  _fmtTime(secs) {
    const s = Math.floor(secs || 0);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
  }

  // ── Undo / Redo ───────────────────────────────────────────────────

  _snapshot() {
    return {
      cuts: new Map(this.editState.cuts),
      mutes: new Map(this.editState.mutes),
      volume: JSON.parse(JSON.stringify(this.editState.volume)),
    };
  }

  _pushUndo() {
    this._undoStack.push(this._snapshot());
    this._redoStack = [];
    this._updateUndoRedoUI();
  }

  _applySnapshot(snap) {
    this.editState.cuts = new Map(snap.cuts);
    this.editState.mutes = new Map(snap.mutes);
    this.editState.volume = JSON.parse(JSON.stringify(snap.volume));

    this.containerTarget.querySelectorAll(".word-token").forEach((token) => {
      const id = token.dataset.id;
      const cut = this.editState.cuts.has(id);
      const muted = this.editState.mutes.has(id);
      const span = token.querySelector(".word-text");
      token.dataset.cut = String(cut);
      token.dataset.muted = String(muted);
      token.classList.toggle("opacity-40", cut);
      if (span) {
        span.classList.toggle("line-through", cut);
        span.classList.toggle("text-gray-400", cut);
        span.classList.toggle("text-gray-800", !cut);
        span.contentEditable = String(!cut);
      }
    });

    this.containerTarget.querySelectorAll(".pause-pill").forEach((pill) => {
      const wrapper = pill.closest(".pause-wrapper");
      if (wrapper)
        this._setPauseCutState(
          pill,
          wrapper,
          this.editState.cuts.has(pill.dataset.id),
        );
    });
  }

  undo() {
    if (this._undoStack.length === 0) return;
    this._redoStack.push(this._snapshot());
    this._applySnapshot(this._undoStack.pop());
    this._updateUndoRedoUI();
    this.markDirty();
  }

  redo() {
    if (this._redoStack.length === 0) return;
    this._undoStack.push(this._snapshot());
    this._applySnapshot(this._redoStack.pop());
    this._updateUndoRedoUI();
    this.markDirty();
  }

  _updateUndoRedoUI() {
    if (this.hasUndoBtnTarget)
      this.undoBtnTarget.disabled = this._undoStack.length === 0;
    if (this.hasRedoBtnTarget)
      this.redoBtnTarget.disabled = this._redoStack.length === 0;
  }

  // ── Global keyboard shortcuts ─────────────────────────────────────

  _onGlobalKeydown(e) {
    // Don't intercept when typing in an input/contenteditable
    const tag = e.target.tagName;
    const editable = e.target.isContentEditable;
    if (
      (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") &&
      e.key !== "Escape"
    )
      return;

    // Arrow keys
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const dir = e.key === "ArrowRight" ? 1 : -1;
      if (editable) {
        // Only intercept when cursor is already at the edge of this word
        if (this._atWordEdge(e.target, dir)) {
          e.preventDefault();
          this._stepWordFromSpan(e.target, dir);
        }
        // Otherwise let the browser move the cursor normally within the word
        return;
      }
      e.preventDefault();
      this._stepWord(dir);
      return;
    }

    if (editable) return;

    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key === "z" && !e.shiftKey) {
      e.preventDefault();
      this.undo();
    }
    if (mod && (e.key === "Z" || (e.key === "z" && e.shiftKey))) {
      e.preventDefault();
      this.redo();
    }
    if (mod && e.key === "y") {
      e.preventDefault();
      this.redo();
    }
    if (
      e.key === "Escape" &&
      this.hasSearchBoxTarget &&
      !this.searchBoxTarget.classList.contains("hidden")
    ) {
      if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
      this._clearSearch();
      this._collapseSearch();
    }
  }

  // Returns true when the caret is at the start (dir=-1) or end (dir=1) of a contenteditable span
  _atWordEdge(span, dir) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
    const range = sel.getRangeAt(0);
    if (dir === 1) {
      const node = range.endContainer;
      const len =
        node.nodeType === Node.TEXT_NODE ? node.length : node.childNodes.length;
      return range.endOffset >= len;
    } else {
      return range.startOffset === 0;
    }
  }

  // Navigate from a focused word-text span to the adjacent word, then place caret there
  _stepWordFromSpan(span, dir) {
    const token = span.closest(".word-token");
    if (!token) return;

    const idx = this._wordIndex.findIndex((w) => w.el === token);
    if (idx === -1) return;

    let next = idx + dir;
    while (next >= 0 && next < this._wordIndex.length) {
      if (this._wordIndex[next].el.dataset.cut !== "true") break;
      next += dir;
    }
    if (next < 0 || next >= this._wordIndex.length) return;

    const w = this._wordIndex[next];
    if (this._currentWord) this._unhighlightWord(this._currentWord);
    this._highlightWord(w.el);
    this._currentWord = w.el;

    if (this.hasPlayerTarget) {
      this.playerTarget.currentTime = w.from / 1000;
      this._scheduleSkips();
    }
    w.el.scrollIntoView({ behavior: "smooth", block: "center" });

    const nextSpan = w.el.querySelector(".word-text");
    if (!nextSpan) return;

    nextSpan.focus();
    // Use setTimeout so our placement runs after the browser's default focus-select
    setTimeout(() => {
      const sel = window.getSelection();
      const textNode = nextSpan.firstChild;
      const range = document.createRange();
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        range.setStart(textNode, dir === 1 ? 0 : textNode.length);
      } else {
        range.setStart(nextSpan, 0);
      }
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  }

  _stepWord(dir) {
    if (!this._wordIndex || this._wordIndex.length === 0) return;

    // Find the current index — prefer the highlighted word, fall back to playback position
    let idx = this._currentWord
      ? this._wordIndex.findIndex((w) => w.el === this._currentWord)
      : -1;

    if (idx === -1) {
      const nowMs = this.hasPlayerTarget
        ? this.playerTarget.currentTime * 1000
        : 0;
      idx = this._wordIndex.findIndex((w) => w.from >= nowMs);
      if (idx === -1) idx = this._wordIndex.length - 1;
      // When stepping backward with no current word, start from found position (don't pre-step)
      if (dir === -1 && idx > 0) idx++;
    }

    // Walk in direction, skipping cut words
    let next = idx + dir;
    while (next >= 0 && next < this._wordIndex.length) {
      if (this._wordIndex[next].el.dataset.cut !== "true") break;
      next += dir;
    }
    if (next < 0 || next >= this._wordIndex.length) return;

    const w = this._wordIndex[next];
    if (this._currentWord) this._unhighlightWord(this._currentWord);
    this._highlightWord(w.el);
    this._currentWord = w.el;

    if (this.hasPlayerTarget) {
      this.playerTarget.currentTime = w.from / 1000;
      this._scheduleSkips();
    }
    w.el.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // ── Search ────────────────────────────────────────────────────────

  openSearch() {
    if (this.hasSearchToggleBtnTarget)
      this.searchToggleBtnTarget.classList.add("hidden");
    if (this.hasSearchBoxTarget)
      this.searchBoxTarget.classList.remove("hidden");
    if (this.hasSearchInputTarget) this.searchInputTarget.focus();
  }

  _collapseSearch() {
    if (this.hasSearchBoxTarget) this.searchBoxTarget.classList.add("hidden");
    if (this.hasSearchToggleBtnTarget)
      this.searchToggleBtnTarget.classList.remove("hidden");
  }

  search(event) {
    const q = event.currentTarget.value.trim().toLowerCase();
    this._clearSearch();
    if (!q) {
      this._collapseSearch();
      return;
    }

    this._searchMatches = [];
    this.containerTarget
      .querySelectorAll(".word-token:not([data-cut='true']) .word-text")
      .forEach((span) => {
        if (span.textContent.trim().toLowerCase().includes(q)) {
          span.classList.add("search-match");
          this._searchMatches.push(span);
        }
      });

    this._searchIndex = 0;
    if (this._searchMatches.length > 0) this._activateSearchMatch(0);
    else this._updateSearchCount();
  }

  onSearchKeydown(event) {
    if (this._searchMatches.length === 0) return;
    if (event.key === "Enter") {
      event.preventDefault();
      const next = event.shiftKey
        ? (this._searchIndex - 1 + this._searchMatches.length) %
          this._searchMatches.length
        : (this._searchIndex + 1) % this._searchMatches.length;
      this._activateSearchMatch(next);
    }
  }

  _activateSearchMatch(idx) {
    this._searchMatches.forEach((span, i) => {
      span.classList.toggle("search-match-active", i === idx);
    });
    this._searchIndex = idx;
    this._searchMatches[idx]
      ?.closest(".word-token")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    this._updateSearchCount();
  }

  _clearSearch() {
    this.containerTarget.querySelectorAll(".search-match").forEach((span) => {
      span.classList.remove("search-match", "search-match-active");
    });
    this._searchMatches = [];
    this._searchIndex = 0;
    this._updateSearchCount();
  }

  _updateSearchCount() {
    if (!this.hasSearchCountTarget) return;
    const n = this._searchMatches.length;
    this.searchCountTarget.textContent =
      n === 0 ? "" : `${this._searchIndex + 1}/${n}`;
  }
}
